import { useState, useCallback, useMemo } from "react";
import { Pencil, FileText, User, AlertCircle, Clock, CalendarClock, Calendar, ListChecks, X } from "lucide-react";

import { Button, DateTimePicker, Input, Label, Textarea } from "@/atoms";
import { TaskPriority } from "@/enums";
import type { TaskInterface, TaskLiteInterface, UpdateTaskPayloadInterface, UserLiteInterface } from "@/interfaces";
import { t, useUpdateTask, useFormErrors, useTasksListLite } from "@/hooks";
import { useSprintStore } from "@/store";
import { nowLocalDT, toLocalDT } from "@/utils";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
} from "@/ui";

const PRIORITY_OPTIONS = [
    { value: TaskPriority.Low, label: "Low" },
    { value: TaskPriority.Medium, label: "Medium" },
    { value: TaskPriority.High, label: "High" },
    { value: TaskPriority.Critical, label: "Critical" },
];

interface SingleTaskSelectProps {
    tasks: TaskLiteInterface[];
    value: string;
    onChange: (id: string) => void;
    isLoading?: boolean;
}

interface UsersAutocompleteProps {
    members: UserLiteInterface[];
    values: string[];
    onChange: (ids: string[]) => void;
}

const UsersAutocomplete = ({ members, values, onChange }: UsersAutocompleteProps) => {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const selected = members.filter((m) => values.includes(m.id));
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return members.filter((m) => !values.includes(m.id) && (!q || m.full_name.toLowerCase().includes(q))).slice(0, 8);
    }, [members, values, query]);

    return (
        <div className="flex flex-col gap-2">
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selected.map((m) => (
                        <span key={m.id} className="flex items-center gap-1 text-xs font-medium bg-primary/10 text-primary rounded-full ps-2 pe-1 py-0.5">
                            {m.full_name}
                            <button type="button" onClick={() => onChange(values.filter((v) => v !== m.id))} className="hover:text-error transition-colors">
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div className="relative">
                <Input
                    placeholder={t("Search users to notify...")}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 120)}
                />
                {open && filtered.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                        {filtered.map((m) => (
                            <button key={m.id} type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { onChange([...values, m.id]); setQuery(""); }}
                                className="w-full px-3 py-2 text-start text-sm hover:bg-muted cursor-pointer flex items-center gap-2">
                                <span className="text-xs font-medium bg-muted rounded-full h-5 w-5 flex items-center justify-center shrink-0">{m.avatar_initials}</span>
                                <span className="flex-1 truncate">{m.full_name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const SingleTaskSelect = ({ tasks, value, onChange, isLoading }: SingleTaskSelectProps) => {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const selected = tasks.find((tk) => tk.id === value);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return tasks.filter((tk) => tk.id !== value && (!q || tk.title.toLowerCase().includes(q) || (tk.code ?? "").toLowerCase().includes(q))).slice(0, 8);
    }, [tasks, value, query]);

    return (
        <div className="flex flex-col gap-2">
            {selected && (
                <span className="flex items-center gap-1 text-xs font-medium bg-primary/10 text-primary rounded-full ps-2 pe-1 py-0.5 w-fit">
                    {selected.code ? `${selected.code} - ${selected.title}` : selected.title}
                    <button type="button" onClick={() => onChange("")} className="hover:text-error transition-colors">
                        <X className="h-3 w-3" />
                    </button>
                </span>
            )}
            <div className="relative">
                <Input
                    placeholder={isLoading ? t("Loading tasks...") : selected ? t("Search to replace...") : t("Search prerequisite task...")}
                    value={query}
                    disabled={isLoading}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 120)}
                />
                {open && !isLoading && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                        {filtered.length > 0 ? filtered.map((tk) => (
                            <button key={tk.id} type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { onChange(tk.id); setQuery(""); setOpen(false); }}
                                className="w-full px-3 py-2 text-start text-sm hover:bg-muted cursor-pointer flex items-center gap-2">
                                {!!tk.code && <span className="text-xs text-text-muted font-mono shrink-0">{tk.code}</span>}
                                <span className="truncate">{tk.title}</span>
                            </button>
                        )) : (
                            <p className="px-3 py-2 text-xs text-text-muted italic">{query ? t("No tasks match your search.") : t("No tasks available.")}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

interface EditTaskDialogProps {
    task: TaskInterface;
    members: UserLiteInterface[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    patchTaskLocal: (id: string, updates: Partial<TaskInterface>) => void;
}

export const EditTaskDialog = ({ task, members, open, onOpenChange, patchTaskLocal }: EditTaskDialogProps) => {
    const { setFieldErrors, clearError, clear: clearFieldErrors, getError } = useFormErrors();
    const { updateHandler, isLoading } = useUpdateTask({ onFieldErrors: setFieldErrors });
    const { activeSprintId } = useSprintStore();
    const { tasks: rawTasks, isLoading: tasksLoading } = useTasksListLite({ sprint_id: activeSprintId ?? undefined, exclude_done: true });
    const sprintTasks = rawTasks.filter((tk) => tk.id !== task.id && tk.status !== "production");

    const [title, setTitle] = useState(task.title ?? "");
    const [description, setDescription] = useState(task.description ?? "");
    const [assignedTo, setAssignedTo] = useState(task.assignee?.id ?? "");
    const [priority, setPriority] = useState<TaskPriority>((task.priority as TaskPriority) ?? TaskPriority.Medium);
    const [estimatedHours, setEstimatedHours] = useState(task.estimated_hours ?? 0);
    const [startTime, setStartTime] = useState(toLocalDT(task.start_time));
    const [deadTime, setDeadTime] = useState(toLocalDT(task.dead_time));
    const [taskType, setTaskType] = useState<"standalone" | "compound">(task.task_type ?? "standalone");
    const [dependsOnTask, setDependsOnTask] = useState(
        typeof task.depends_on_task === "string" ? task.depends_on_task : (task.depends_on_task?.id ?? "")
    );
    const [notifyOnDone, setNotifyOnDone] = useState(task.notify_on_done ?? false);
    const [notifyUserIds, setNotifyUserIds] = useState<string[]>([]);

    const handleOpenChange = useCallback((next: boolean) => {
        if (next) {
            setTitle(task.title ?? "");
            setDescription(task.description ?? "");
            setAssignedTo(task.assignee?.id ?? "");
            setPriority((task.priority as TaskPriority) ?? TaskPriority.Medium);
            setEstimatedHours(task.estimated_hours ?? 0);
            setStartTime(toLocalDT(task.start_time));
            setDeadTime(toLocalDT(task.dead_time));
            setTaskType(task.task_type ?? "standalone");
            setDependsOnTask(typeof task.depends_on_task === "string" ? task.depends_on_task : (task.depends_on_task?.id ?? ""));
            setNotifyOnDone(task.notify_on_done ?? false);
            setNotifyUserIds([]);
            clearFieldErrors();
        }
        onOpenChange(next);
    }, [task, onOpenChange, clearFieldErrors]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        const clientErrors: Record<string, string[]> = {};
        if (!title.trim()) clientErrors.title = [t("Title is required")];
        if (!assignedTo) clientErrors.assigned_to = [t("Assignee is required")];
        if (!estimatedHours || estimatedHours <= 0) clientErrors.estimated_hours = [t("Estimated hours is required")];
        if (Object.keys(clientErrors).length > 0) { setFieldErrors(clientErrors); return; }

        const payload: UpdateTaskPayloadInterface = {
            title: title.trim(),
            description: description.trim() || undefined,
            assigned_to: assignedTo,
            priority,
            estimated_hours: estimatedHours,
            start_time: startTime ? new Date(startTime).toISOString() : null,
            dead_time: deadTime ? new Date(deadTime).toISOString() : null,
            task_type: taskType,
            depends_on_task: taskType === "compound" && dependsOnTask ? dependsOnTask : null,
            notify_on_done: taskType === "compound" ? notifyOnDone : false,
            notify_user_ids: taskType === "compound" && notifyOnDone ? notifyUserIds : [],
        };

        const updated = await updateHandler(task.id, payload);
        if (updated) {
            patchTaskLocal(task.id, updated as Partial<TaskInterface>);
            onOpenChange(false);
        }
    }, [title, description, assignedTo, priority, estimatedHours, startTime, deadTime, taskType, dependsOnTask, notifyOnDone, task.id, updateHandler, patchTaskLocal, onOpenChange, setFieldErrors]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center gap-2 mb-1">
                        <Pencil className="h-5 w-5 text-primary" />
                        <DialogTitle>{t("Edit Task")}</DialogTitle>
                    </div>
                    <DialogDescription>{t("Update task details")}</DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                        <Label htmlFor="edit-title" className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-text-muted" />
                            {t("Title")} <span className="text-error">*</span>
                        </Label>
                        <Input id="edit-title" value={title} onChange={(e) => { setTitle(e.target.value); clearError("title"); }} />
                        {getError("title") && <p className="text-xs text-error">{getError("title")}</p>}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="edit-description" className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-text-muted" />
                            {t("Description")}
                        </Label>
                        <Textarea id="edit-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-assignee" className="flex items-center gap-2">
                                <User className="h-4 w-4 text-text-muted" />
                                {t("Assigned To")} <span className="text-error">*</span>
                            </Label>
                            <Select value={assignedTo} onValueChange={(v) => { setAssignedTo(v); clearError("assigned_to"); }}>
                                <SelectTrigger id="edit-assignee" className="w-full">
                                    <SelectValue placeholder={t("Select assignee")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {members.map((m) => (
                                        <SelectItem key={m.id} value={m.id}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium bg-muted rounded-full h-5 w-5 flex items-center justify-center">{m.avatar_initials}</span>
                                                <span>{m.full_name}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {getError("assigned_to") && <p className="text-xs text-error">{getError("assigned_to")}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="edit-priority" className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-text-muted" />
                                {t("Priority")}
                            </Label>
                            <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
                                <SelectTrigger id="edit-priority" className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PRIORITY_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{t(o.label)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="edit-taskType" className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-text-muted" />
                                {t("Task Type")}
                            </Label>
                            <Select value={taskType} onValueChange={(v) => setTaskType(v as "standalone" | "compound")}>
                                <SelectTrigger id="edit-taskType" className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="standalone">{t("Standalone")}</SelectItem>
                                    <SelectItem value="compound">{t("Compound")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="edit-hours" className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-text-muted" />
                                {t("Estimated Hours")} <span className="text-error">*</span>
                            </Label>
                            <Input
                                id="edit-hours"
                                type="number"
                                min={0.5}
                                step={0.5}
                                value={estimatedHours || ""}
                                onChange={(e) => {
                                    const hours = parseFloat(e.target.value) || 0;
                                    const prevHours = estimatedHours || 0;
                                    setEstimatedHours(hours);
                                    clearError("estimated_hours");
                                    if (hours > 0) {
                                        const pad = (n: number) => String(n).padStart(2, "0");
                                        let dead: Date;
                                        if (deadTime && prevHours > 0) {
                                            dead = new Date(new Date(deadTime).getTime() + (hours - prevHours) * 3600 * 1000);
                                        } else {
                                            const base = startTime ? new Date(startTime) : new Date();
                                            dead = new Date(base.getTime() + hours * 3600 * 1000);
                                        }
                                        dead.setSeconds(0, 0);
                                        setDeadTime(`${dead.getFullYear()}-${pad(dead.getMonth() + 1)}-${pad(dead.getDate())}T${pad(dead.getHours())}:${pad(dead.getMinutes())}`);
                                    }
                                }}
                            />
                            {getError("estimated_hours") && <p className="text-xs text-error">{getError("estimated_hours")}</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-startTime" className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-text-muted" />
                                {t("Start Time")}
                            </Label>
                            <DateTimePicker id="edit-startTime" min={nowLocalDT()} value={startTime} onChange={setStartTime} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-deadline" className="flex items-center gap-2">
                                <CalendarClock className="h-4 w-4 text-text-muted" />
                                {t("Dead Time")}
                            </Label>
                            <DateTimePicker id="edit-deadline" min={nowLocalDT()} value={deadTime} onChange={setDeadTime} />
                        </div>
                    </div>

                    {taskType === "compound" && (
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <ListChecks className="h-4 w-4 text-text-muted" />
                                {t("Prerequisite Task")}
                            </Label>
                            <p className="text-xs text-text-muted">{t("Task that must be done before this one can start.")}</p>
                            <SingleTaskSelect
                                tasks={sprintTasks}
                                value={dependsOnTask}
                                onChange={setDependsOnTask}
                                isLoading={tasksLoading}
                            />
                        </div>
                    )}

                    {taskType === "compound" && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="edit-notifyEnabled"
                                    checked={notifyOnDone}
                                    onChange={(e) => setNotifyOnDone(e.target.checked)}
                                    className="h-4 w-4 rounded border-border accent-primary"
                                />
                                <Label htmlFor="edit-notifyEnabled" className="cursor-pointer text-sm font-medium">
                                    {t("Notify others when done")}
                                </Label>
                            </div>
                            {notifyOnDone && (
                                <UsersAutocomplete
                                    members={members}
                                    values={notifyUserIds}
                                    onChange={setNotifyUserIds}
                                />
                            )}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t border-border">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
                            {t("Cancel")}
                        </Button>
                        <Button type="submit" loading={isLoading} className="gap-2">
                            {!isLoading && <Pencil className="h-4 w-4" />}
                            {t("Save Changes")}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};
